<script lang="ts">
  import { onMount } from 'svelte';
  import { X, Calendar, Clock } from 'lucide-svelte';
  import { _ } from 'svelte-i18n';
  import Card from '../ui/card.svelte';
  import { supabase } from '../../lib/supabase';
  import type { Database } from '../../integrations/supabase/types';

  type TestResult = Database['public']['Tables']['test_results']['Row'];

  let {
    open = false,
    onClose
  }: {
    open: boolean;
    onClose: () => void;
  } = $props();

  let testHistory = $state<TestResult[]>([]);
  let isLoadingHistory = $state(false);

  async function loadTestHistory() {
    try {
      isLoadingHistory = true;
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        console.log('User not authenticated, skipping test history load');
        return;
      }

      const { data, error } = await supabase
        .from('test_results')
        .select('*')
        .eq('user_id', user.id)
        .order('completed_at', { ascending: false })
        .limit(10);

      if (error) {
        console.error('Error loading test history:', error);
        return;
      }

      testHistory = data || [];
    } catch (error) {
      console.error('Error loading test history:', error);
    } finally {
      isLoadingHistory = false;
    }
  }

  // Load test history when dialog opens
  $effect(() => {
    if (open) {
      loadTestHistory();
    }
  });

  function formatDate(dateString: string | null): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  function formatTime(seconds: number | null): string {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  function handleClose() {
    onClose?.();
  }

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      handleClose();
    }
  }
</script>

{#if open}
  <div
    class="fixed inset-0 z-[96] flex flex-col bg-background"
    role="dialog"
    aria-modal="true"
    tabindex="0"
    onkeydown={handleKeydown}
  >
    <div class="flex items-start justify-between px-6 pt-6 pb-4 gap-4">
      <div class="space-y-2 flex-1">
        <p class="text-xs uppercase tracking-[0.28em] text-muted-foreground break-words">{$_('test.test_history')}</p>
        <h2 class="text-2xl font-semibold text-foreground break-words">{$_('test.test_history')}</h2>
      </div>
      <button
        class="btn btn-close"
        onclick={onClose}
        aria-label={$_('aria.close')}
      >
        <X class="h-5 w-5" />
      </button>
    </div>

    <div class="flex-1 overflow-y-auto px-6 pb-24">
      {#if isLoadingHistory}
        <Card class="widget-card tone-list p-6">
          <p class="text-muted-foreground">{$_('test.loading_history')}</p>
        </Card>
      {:else if testHistory.length === 0}
        <Card class="widget-card tone-list p-6">
          <p class="font-medium mb-2">{$_('test.no_tests_yet')}</p>
          <p class="text-sm text-muted-foreground">{$_('test.results_will_show_here')}</p>
        </Card>
      {:else}
        <div class="space-y-3 pb-16">
          {#each testHistory as result}
            <Card class="widget-card tone-list p-4">
              <div class="flex items-center justify-between mb-3">
                <div class="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar class="w-4 h-4" />
                  <span>{formatDate(result.completed_at)}</span>
                </div>
                <div class="text-2xl font-bold {result.score_percentage >= 70 ? 'text-primary' : result.score_percentage >= 50 ? 'text-warning' : 'text-destructive'}">
                  {Math.round(result.score_percentage)}%
                </div>
              </div>
              <div class="flex items-center justify-between text-sm">
                <span class="text-muted-foreground">
                  {result.correct_answers} / {result.total_questions} {$_('test.correct_answers')}
                </span>
                {#if result.time_taken_seconds}
                  <div class="flex items-center gap-1 text-muted-foreground">
                    <Clock class="w-3 h-3" />
                    <span>{formatTime(result.time_taken_seconds)}</span>
                  </div>
                {/if}
              </div>
            </Card>
          {/each}
        </div>
      {/if}
    </div>
  </div>
{/if}
